import React, { useState } from 'react';
import {
    Paper,
    Typography,
    Box,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Alert,
    CircularProgress,
    Divider,
} from '@mui/material';
import {
    PersonAdd as PersonAddIcon,
    Delete as DeleteIcon,
    People as PeopleIcon,
} from '@mui/icons-material';
import {
    useGlobalniPrijemciQuery,
    useAddGlobalniPrijemceMutation,
    useRemoveGlobalniPrijemceMutation,
} from '../../api/JsdhNotifikaceService/service/IotMutations';
import type { CreateIotPrijemceRequest } from '../../api/JsdhNotifikaceService/generated';
import PrijemceFormDialog from './PrijemceFormDialog';

const GlobalPrijemciSection: React.FC = () => {
    const [dialogOpen, setDialogOpen] = useState(false);

    const { data: prijemci, isLoading } = useGlobalniPrijemciQuery();
    const addMutation = useAddGlobalniPrijemceMutation();
    const removeMutation = useRemoveGlobalniPrijemceMutation();

    const handleAddPrijemce = (data: CreateIotPrijemceRequest) => {
        addMutation.mutate(data, {
            onSuccess: () => {
                setDialogOpen(false);
            },
        });
    };

    const handleRemovePrijemce = (prijemceId: number) => {
        removeMutation.mutate(prijemceId);
    };

    if (isLoading) {
        return (
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            </Paper>
        );
    }

    return (
        <>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon color="primary" />
                        <Typography variant="h6">
                            Globální příjemci IoT notifikací ({prijemci?.length || 0})
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                        onClick={() => setDialogOpen(true)}
                        size="small"
                    >
                        Přidat příjemce
                    </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Alert severity="info" sx={{ mb: 2 }}>
                    Tito příjemci dostávají provozní upozornění (nízká baterie, výpadek čidla, teplota).
                    Požární/CO poplach je vždy odeslán celé jednotce.
                    {' '}Platí pro <strong>všechny</strong> monitorované objekty — specifické příjemce pro jednotlivé objekty
                    můžete nastavit v detailu objektu.
                </Alert>

                {prijemci?.length ? (
                    <List dense>
                        {prijemci.map((p) => (
                            <ListItem
                                key={p.id}
                                sx={{
                                    bgcolor: 'grey.50',
                                    borderRadius: 1,
                                    mb: 0.5,
                                }}
                            >
                                <ListItemText
                                    primary={p.uzivatelJmeno || p.email || `Uživatel #${p.uzivatelId}`}
                                    secondary={p.uzivatelId ? p.email : null}
                                />
                                <ListItemSecondaryAction>
                                    <IconButton
                                        edge="end"
                                        onClick={() => handleRemovePrijemce(p.id)}
                                        disabled={removeMutation.isPending}
                                        size="small"
                                        color="error"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Žádní globální příjemci. Přidejte příjemce, kteří budou dostávat notifikace ze všech objektů.
                    </Typography>
                )}
            </Paper>

            <PrijemceFormDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSubmit={handleAddPrijemce}
                isLoading={addMutation.isPending}
                title="Přidat globálního příjemce"
            />
        </>
    );
};

export default GlobalPrijemciSection;
